import { scheduleIdle } from "@/lib/scheduleIdle";

type AmplitudeModule = typeof import("@amplitude/analytics-browser");

let initialized = false;
let loadPromise: Promise<AmplitudeModule> | null = null;
let replayAttached = false;
let checkoutReplayRequested = false;

// Replay's DOM instrumentation is ~100–190ms of main-thread work; keep it out
// of the interactivity window. Sampled sessions lose their first few seconds.
export const SESSION_REPLAY_DELAY_MS = 4000;

// Sampling is decided before the replay SDK is downloaded, so unsampled
// visitors never pay for it. Checkout bypasses the gate (records at 100%).
export const SESSION_REPLAY_SAMPLE_RATE = 0.1;

// Amplitude's device id should match Segment's anonymousId so both tools
// attribute a visitor to the same identity. Segment's anonymousId isn't
// available until Segment's own SDK finishes loading (a network round trip),
// so cap the wait — a slow/broken Segment load must not hang Amplitude's init.
export const ANONYMOUS_ID_TIMEOUT_MS = 3000;

async function resolveDeviceId(getAnonymousId: () => Promise<string | undefined>): Promise<string | undefined> {
  try {
    return await Promise.race([
      getAnonymousId(),
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), ANONYMOUS_ID_TIMEOUT_MS)),
    ]);
  } catch {
    return undefined;
  }
}

// Init @amplitude/analytics-browser directly, not @amplitude/unified — unified
// force-installs Session Replay + Engagement + Experiment plugins on init.
export function initAmplitude(apiKey: string | undefined, getAnonymousId: () => Promise<string | undefined>) {
  if (!apiKey || initialized) return;
  initialized = true;
  scheduleIdle(() => {
    loadPromise = loadAndInit(apiKey, getAnonymousId);
  });
}

// Safe to call before init runs (checkout landings): the request is honored
// once init completes.
export function ensureCheckoutSessionReplay() {
  checkoutReplayRequested = true;
  if (loadPromise) {
    void loadPromise.then(attachSessionReplay);
  }
}

// Keyed to the session id so every page load in a session flips the same way —
// a per-load Math.random() would fragment sessions into partial recordings.
export function isSessionSampled(sessionId: number | undefined, sampleRate: number): boolean {
  if (sessionId === undefined) return false;
  return (sessionId % 1000) / 1000 < sampleRate;
}

async function loadAndInit(
  apiKey: string,
  getAnonymousId: () => Promise<string | undefined>,
): Promise<AmplitudeModule> {
  const amplitude = await import("@amplitude/analytics-browser");
  const deviceId = await resolveDeviceId(getAnonymousId);
  await amplitude.init(apiKey, {
    autocapture: {
      attribution: true,
      pageViews: true,
      sessions: true,
      formInteractions: false,
      fileDownloads: false,
      elementInteractions: false,
      networkTracking: false,
      pageUrlEnrichment: true,
      webVitals: false,
    },
    deviceId,
  }).promise;
  if (checkoutReplayRequested) {
    void attachSessionReplay(amplitude);
  } else {
    setTimeout(() => {
      if (!isSessionSampled(amplitude.getSessionId(), SESSION_REPLAY_SAMPLE_RATE)) return;
      void attachSessionReplay(amplitude);
    }, SESSION_REPLAY_DELAY_MS);
  }
  return amplitude;
}

async function attachSessionReplay(amplitude: AmplitudeModule) {
  if (replayAttached) return;
  replayAttached = true;
  const { sessionReplayPlugin } = await import("@amplitude/plugin-session-replay-browser");
  // sampleRate 1: sampling already happened (gate or checkout bypass).
  void amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));
}
